import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ZONES } from "@/data/site";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function toForm(t) {
  return {
    title: t.title || "", destination: t.destination || "", region: t.region || "india", zone: t.zone || "",
    categories: (t.categories || ["group"]).join(", "), nights: t.nights ?? 3, days: t.days ?? 4,
    start_location: t.start_location || "", end_location: t.end_location || "",
    price: t.price ?? "", original_price: t.original_price ?? "", image: t.image || "", badge: t.badge || "",
    overview: t.overview || "", dates: (t.dates || []).join("\n"), gallery: (t.gallery || []).join("\n"),
    highlights: (t.highlights || []).join("\n"), inclusions: (t.inclusions || []).join("\n"), exclusions: (t.exclusions || []).join("\n"),
    itinerary: (t.itinerary || []).map((d) => `${d.title} | ${d.description}`).join("\n"),
    featured: !!t.featured, active: t.active ?? true,
  };
}

function toPayload(f) {
  return {
    ...f,
    categories: f.categories.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean),
    nights: Number(f.nights), days: Number(f.days), price: Number(f.price),
    original_price: f.original_price ? Number(f.original_price) : null,
    dates: lines(f.dates), gallery: lines(f.gallery), highlights: lines(f.highlights),
    inclusions: lines(f.inclusions), exclusions: lines(f.exclusions),
    itinerary: lines(f.itinerary).map((l, i) => {
      const [title, ...rest] = l.split("|");
      return { day: i + 1, title: title.trim(), description: rest.join("|").trim() };
    }),
  };
}

const inp = "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-2.5 text-cream outline-none focus:border-terra-soft placeholder:text-cream/40";

function F({ label, k, form, set, type = "text", area, rows = 3, hint, span }) {
  return (
    <label className={`block space-y-1.5 ${span ? "sm:col-span-2" : ""}`}>
      <span className="eyebrow text-cream/70">{label}</span>
      {area ? (
        <textarea rows={rows} value={form[k]} onChange={set(k)} className={inp} data-testid={`trip-form-${k}`} placeholder={hint} />
      ) : (
        <input type={type} value={form[k]} onChange={set(k)} className={inp} data-testid={`trip-form-${k}`} placeholder={hint}
          required={["title", "destination", "price", "image"].includes(k)} />
      )}
    </label>
  );
}

export function TripFormDialog({ trip, onClose, onSaved }) {
  const [form, setForm] = useState(() => toForm(trip));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const p = { form, set };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (trip.id) await api.put(`/admin/trips/${trip.id}`, toPayload(form));
      else await api.post("/admin/trips", toPayload(form));
      toast.success(trip.id ? "Trip updated" : "Trip created");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#17120F] text-cream border-cream/10 rounded-3xl" data-testid="trip-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-extrabold">{trip.id ? "Edit trip" : "New trip"}</DialogTitle>
          <DialogDescription className="text-cream/60">Lists take one item per line. Itinerary: "Title | description" per day.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <F label="Title" k="title" span {...p} />
          <F label="Destination" k="destination" hint="e.g. Georgia" {...p} />
          <label className="block space-y-1.5">
            <span className="eyebrow text-cream/70">Region</span>
            <select value={form.region} onChange={set("region")} className={inp} data-testid="trip-form-region">
              <option className="bg-ink" value="india">India</option>
              <option className="bg-ink" value="international">International</option>
            </select>
          </label>
          <label className="block space-y-1.5">
            <span className="eyebrow text-cream/70">Explore region</span>
            <select value={form.zone} onChange={set("zone")} className={inp} data-testid="trip-form-zone">
              <option className="bg-ink" value="">None</option>
              {ZONES.map((z) => <option key={z.slug} className="bg-ink" value={z.slug}>{z.name}</option>)}
            </select>
          </label>
          <F label="Categories (comma separated)" k="categories" hint="group, georgia, honeymoon, weekend" {...p} />
          <F label="Badge" k="badge" hint="Popular, New Year…" {...p} />
          <F label="Nights" k="nights" type="number" {...p} />
          <F label="Days" k="days" type="number" {...p} />
          <F label="Start location" k="start_location" {...p} />
          <F label="End location" k="end_location" {...p} />
          <F label="Price (₹)" k="price" type="number" {...p} />
          <F label="Original price (₹)" k="original_price" type="number" {...p} />
          <F label="Cover image URL" k="image" span {...p} />
          <F label="Overview" k="overview" area span {...p} />
          <F label="Departure dates" k="dates" area hint="17 Oct 2026" {...p} />
          <F label="Gallery image URLs" k="gallery" area {...p} />
          <F label="Highlights" k="highlights" area {...p} />
          <F label="Itinerary" k="itinerary" area rows={5} hint="Arrive in Tbilisi | Airport pickup…" {...p} />
          <F label="Inclusions" k="inclusions" area {...p} />
          <F label="Exclusions" k="exclusions" area {...p} />
          <div className="sm:col-span-2 flex flex-wrap gap-8 pt-2">
            <label className="flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="trip-form-featured" /> Featured</label>
            <label className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="trip-form-active" /> Visible on site</label>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="rounded-full border border-cream/20 px-5 py-2.5" data-testid="trip-form-cancel">Cancel</button>
            <button disabled={busy} className="rounded-full bg-terra px-6 py-2.5 font-semibold disabled:opacity-60" data-testid="trip-form-submit">
              {busy ? "Saving…" : "Save trip"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
